import { useIsProjectTechnicalWriter } from "@/auth/permissions";
import { GoalField } from "@/components/GoalField";
import { TriggerIntroduction, TriggerSectionTitle } from "@/features/triggers/components/TriggerGuidance";
import FormPanel from "@imminently/decisively-ui/FormPanel";
import SelectField from "@imminently/decisively-ui/SelectField";
import TextArea from "@imminently/decisively-ui/TextArea";
import TextField from "@imminently/decisively-ui/TextField";
import { TriggerScheduling } from "./TriggerScheduling";

const severityOptions = [
  { value: "info", label: "Info" },
  { value: "warning", label: "Warning" },
  { value: "critical", label: "Critical" },
];

const threadOptions = [
  { value: "decisively", label: "Decisively" },
  { value: "edward", label: "EdWard" },
];

export const AttributeNotificationForm = ({ scope }: { scope: string }) => {
  const canEdit = useIsProjectTechnicalWriter();
  const scopedValue = (name: string) => `${scope}.${name}`;

  return (
    <div className="flex flex-col gap-4">
      <TriggerIntroduction type="notification" />
      <FormPanel>
        <TriggerSectionTitle>Message</TriggerSectionTitle>
        <FormPanel>
          <TextField
            label="Title"
            name={scopedValue("title")}
            helperText="Used to identify and organise this trigger in the attribute’s trigger list."
            required
            disabled={!canEdit}
            fullWidth
          />
          <TextField
            label="Subject"
            name={scopedValue("subject")}
            helperText="Shown to recipients as the notification heading in the application."
            required
            disabled={!canEdit}
            fullWidth
          />
          <TextField
            label="Body"
            name={scopedValue("body")}
            required
            disabled={!canEdit}
            inputComponent={TextArea}
            rows={6}
            fullWidth
          />
        </FormPanel>
      </FormPanel>

      <FormPanel>
        <TriggerSectionTitle>Delivery</TriggerSectionTitle>
        <FormPanel>
          <GoalField
            label="Recipient"
            name={scopedValue("recipient")}
            disabled={!canEdit}
          />
          <SelectField
            label="Severity"
            name={scopedValue("severity")}
            options={severityOptions}
            required
            disabled={!canEdit}
            fullWidth
          />
          <GoalField
            label="Due date"
            name={scopedValue("dueDate")}
            disabled={!canEdit}
          />
          <SelectField
            label="Thread"
            name={scopedValue("thread")}
            options={threadOptions}
            allowArbitrary
            searchable
            required
            disabled={!canEdit}
            placeholder="Select an application or type a custom thread"
            helperText="Determines where the notification appears. Select Decisively for notifications in Decisively, or EdWard for notifications in EdWard. For your own application, type its custom thread identifier (or {{ thread attribute }}) and select the new option."
            fullWidth
          />
        </FormPanel>
      </FormPanel>

      <FormPanel>
        <TriggerSectionTitle>Action (optional)</TriggerSectionTitle>
        <FormPanel>
          <TextField
            label="Action label"
            name={scopedValue("action.label")}
            disabled={!canEdit}
            placeholder="View timesheet"
            helperText="Leave blank to use the application’s default button label."
            fullWidth
          />
          <TextField
            label="Action destination"
            name={scopedValue("action.path")}
            disabled={!canEdit}
            placeholder="/employees/{{ employee id }}/{{ start date }}"
            helperText="Use a path within the receiving application. For EdWard requests: /requests?requestId={{ request session id }}. Leave blank for no action button."
            fullWidth
          />
        </FormPanel>
      </FormPanel>

      <FormPanel>
        <TriggerSectionTitle>Scheduling</TriggerSectionTitle>
        <FormPanel>
          <TriggerScheduling
            scope={scope}
            disabled={!canEdit}
          />
        </FormPanel>
      </FormPanel>
    </div>
  );
};
